import { Component, computed, inject, input, OnDestroy, output, signal } from '@angular/core';
import { A11yModule } from '@angular/cdk/a11y';
import { DOCUMENT } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FilterOption, InitialsPipe, TableFilterState } from 'shared';
import { ThemedIconComponent } from '../../atoms/themed-icon/themed-icon.component';
import { AvatarComponent } from '../../molecules/avatar/avatar.component';

/**
 * Full-screen "Filters" page for mobile/narrow layouts, driven by a `TableFilterState` (the same
 * engine used by the desktop dropdown filter bar) so Tasks/Members can share this without
 * reimplementing filter UI. Fields with `mobileSubpage: true` (e.g. Assignee, Reported by) show a
 * collapsed row that opens their own dedicated search sub-page; other fields render inline.
 */
@Component({
  selector: 'ocp-table-filter-mobile-panel',
  imports: [TranslatePipe, ThemedIconComponent, AvatarComponent, InitialsPipe, A11yModule],
  templateUrl: './table-filter-mobile-panel.component.html',
  styleUrl: './table-filter-mobile-panel.component.scss',
  host: {
    class: 'table-filter-mobile-panel',
    role: 'dialog',
    'aria-modal': 'true',
    '[attr.aria-label]': 'title()',
    '(keydown.escape)': 'panelClose.emit()',
  },
})
export class TableFilterMobilePanelComponent<TItem = unknown> implements OnDestroy {
  private readonly document = inject(DOCUMENT);
  private readonly translateService = inject(TranslateService);
  /** Element focused before the panel opened, restored to it on close. */
  private readonly previouslyFocusedElement = this.document.activeElement as HTMLElement | null;

  readonly filterState = input.required<TableFilterState<TItem>>();
  readonly title = input.required<string>();
  readonly showAllLabel = input.required<string>();
  readonly clearAllLabel = input.required<string>();
  readonly applyLabel = input.required<string>();
  /** Label for the "Select" button at the bottom of an Assignee/Reported-by subpage. */
  readonly selectLabel = input.required<string>();
  readonly closeAriaLabel = input.required<string>();
  readonly backAriaLabel = input.required<string>();
  /** Formats the "N selected" label shown for a collapsed subpage field row. */
  readonly selectedCountLabel = input.required<(count: number) => string>();

  readonly panelClose = output<void>();

  protected readonly activeSubpageKey = signal<string | null>(null);
  protected readonly subpageQuery = signal('');

  protected readonly activeField = computed(() =>
    this.filterState().fields.find(field => field.key === this.activeSubpageKey())
  );

  protected readonly filteredSubpageOptions = computed<FilterOption[]>(() => {
    const key = this.activeSubpageKey();
    if (!key) {
      return [];
    }
    const query = this.subpageQuery().trim().toLowerCase();
    const options = this.filterState().getFieldOptions(key)();
    if (!query) {
      return options;
    }
    return options.filter(option =>
      (option.label ?? this.translateService.instant(option.labelKey ?? ''))
        .toLowerCase()
        .includes(query)
    );
  });

  ngOnDestroy(): void {
    this.previouslyFocusedElement?.focus();
  }

  protected openSubpage(fieldKey: string): void {
    this.activeSubpageKey.set(fieldKey);
    this.subpageQuery.set('');
  }

  protected closeSubpage(): void {
    this.activeSubpageKey.set(null);
    this.subpageQuery.set('');
  }

  protected onSubpageSearchInput(event: Event): void {
    this.subpageQuery.set((event.target as HTMLInputElement).value);
  }

  protected trackByFieldKey(_index: number, field: { key: string }): string {
    return field.key;
  }

  protected trackByValue(_index: number, option: FilterOption): string {
    return String(option.value);
  }
}
